import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Slide,
  Stack,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CookieIcon from '@mui/icons-material/Cookie';
import CookieManager from '../../pages/Cookies/CookieManager';
import { saveCookiePreferences, CONSENT_EVENT } from '../../analytics/consent';
import { LegalInline } from '../Legal/LegalPage';

const CookieBanner = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    // Check if user has already made cookie choices
    const cookieChoices = localStorage.getItem('cookiePreferences');
    if (!cookieChoices) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Tercih başka bir yerden (ör. /cookies sayfasındaki panel) kaydedilirse bilgilendirme çubuğunu kapat
  useEffect(() => {
    const hide = () => setShowBanner(false);
    window.addEventListener(CONSENT_EVENT, hide);
    return () => window.removeEventListener(CONSENT_EVENT, hide);
  }, []);

  // saveCookiePreferences: localStorage + Google Consent Mode güncellemesi + sayfa takibine haber verir
  // İşlevsel ve reklam çerezi kullanılmıyor (Çerez Politikası); "Kabul et" yalnızca analitik izni verir,
  // reklam sinyalleri (ad_storage vb.) kapalı kalır.
  const handleAcceptAll = () => {
    saveCookiePreferences({
      essential: true,
      analytics: true,
      functional: false,
      advertising: false
    });
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    saveCookiePreferences({
      essential: true, // Essential cookies can't be rejected
      analytics: false,
      functional: false,
      advertising: false
    });
    setShowBanner(false);
  };

  const handleSavePreferences = (preferences) => {
    saveCookiePreferences(preferences);
    setShowPreferences(false);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.95)
              : alpha(theme.palette.background.paper, 0.95),
            boxShadow: theme.shadows[8],
            backdropFilter: 'blur(8px)',
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            zIndex: theme.zIndex.snackbar
          }}
        >
          <Box
            sx={{
              maxWidth: 'lg',
              mx: 'auto',
              p: 3,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <CookieIcon 
                sx={{ 
                  fontSize: 40,
                  color: theme.palette.primary.main 
                }} 
              />
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('cookies.banner.title', 'We use cookies')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <LegalInline text={t('cookies.banner.description')} />
                </Typography>
              </Box>
            </Box>

            <Stack 
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ 
                minWidth: { sm: '300px' },
                justifyContent: 'flex-end'
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setShowPreferences(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t('cookies.banner.preferences', 'Cookie Settings')}
              </Button>
              {/* Reddet, Kabul et ile aynı görünürlükte olmalı (KVKK Çerez Rehberi) */}
              <Button
                variant="contained"
                onClick={handleRejectAll}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t('cookies.banner.reject', 'Reject All')}
              </Button>
              <Button
                variant="contained"
                onClick={handleAcceptAll}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t('cookies.banner.accept', 'Accept All')}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Slide>

      <Dialog
        open={showPreferences}
        onClose={() => setShowPreferences(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('cookies.manager.title', 'Cookie Preferences')}
        </DialogTitle>
        <DialogContent>
          <CookieManager onSave={handleSavePreferences} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreferences(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieBanner; 